import { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Col, Button, Spin, Radio, Tooltip, Form, Input, TimePicker, Select } from 'antd'
import dayjs from 'dayjs'
// eslint-disable-next-line import/no-extraneous-dependencies
import { InfoCircleOutlined } from '@ant-design/icons'

import { TitleAndDivider } from '@components'
import { useConfigurationProperties } from '@hooks'
import { RoleEnum } from '@types'
import { useUser } from '@contexts'
import { CutoffTimeUtils } from '@utils'

import {
  StyledRow,
  SettingsRowTight,
  SettingsTitleRow,
  Container,
  StyledText,
  StyledInputNumber,
} from './components'
import { validationSchema, FormType } from './validations'

const automaticBatchCloseDayOptions = Array.from({ length: 31 }, (_, index) => {
  const day = String(index + 1)
  return { label: day, value: day }
})

const parseAutomaticBatchCloseDays = (value?: string) => {
  if (!value) return []

  const days = value
    .split(',')
    .map(day => day.trim())
    .filter(day => {
      const dayNumber = parseInt(day, 10)
      return !Number.isNaN(dayNumber) && dayNumber >= 1 && dayNumber <= 31
    })

  const uniqueDays = Array.from(new Set(days)).sort((a, b) => parseInt(a, 10) - parseInt(b, 10))

  return uniqueDays
}

const formatAutomaticBatchCloseDays = (days: string[]) =>
  parseAutomaticBatchCloseDays(days.join(',')).join(',')

const Settings = () => {
  const { state } = useUser()
  const roles = state?.roles.map(role => role.name)
  const { ADMINRO } = RoleEnum
  const { t } = useTranslation()
  const {
    allocationMinutes,
    allocationId,
    isLoading,
    deleteTicketsDays,
    deleteTicketsId,
    assignmentTicketsId,
    assignmentTickets,
    preAuthTicketsId,
    preAuthTickets,
    updateConfigPropsTime,
    updateConfigPropsPercentaje,
    updateConfigPropsAssignmentTickets,
    isConfigLoadingTime,
    isConfigLoadingPercentaje,
    updatePreAuthTickets,
    requirementCCId,
    requirementCCValue,
    updateRequirementCC,
    isConfigLoadingRequirementCC,
    cutoffTimeId,
    cutoffTimeHour,
    updateCutoffTime,
    isConfigLoadingCutoffTime,
    automaticBillingBatchCloseDaysId,
    automaticBillingBatchCloseDays,
    updateAutomaticBillingBatchCloseDays,
    isConfigLoadingAutomaticBillingBatchCloseDays,
    carrierMultiAssignmentId,
    carrierMultiAssignmentEnabled,
    updateCarrierMultiAssignment,
  } = useConfigurationProperties()
  const { control, handleSubmit, setValue } = useForm<FormType>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      allocation: 0,
      ticketDays: 0,
      assignmentTickets: 'false',
      preAuthTickets: 'false',
      requirementCC: '',
      cutoffTime: CutoffTimeUtils.DEFAULT_CUTOFF_TIME,
      automaticBillingBatchCloseDays: [],
    },
  })
  const [newAllocationTime, setNewAllocationTime] = useState<number>()
  const [newDeleteTicketDays, setNewDeleteTicketDays] = useState<number>()
  const [newRequirementCCValue, setNewRequirementCCValue] = useState<string>()
  const [newCutoffTime, setNewCutoffTime] = useState<string>()
  const [newAutomaticBatchCloseDays, setNewAutomaticBatchCloseDays] = useState<string[]>([])

  const handleAllocationUpdate = (form: FormType) => {
    if (allocationId && allocationId !== newAllocationTime)
      updateConfigPropsTime({ id: allocationId, value: form.allocation })
  }

  const handleDeleteTicketsUpdate = (form: FormType) => {
    if (deleteTicketsId && deleteTicketsId !== newDeleteTicketDays)
      updateConfigPropsPercentaje({ id: deleteTicketsId, value: form.ticketDays })
  }
  const handleAssignmentTicketsUpdate = (value: number | undefined) => {
    updateConfigPropsAssignmentTickets({
      id: assignmentTicketsId,
      value: value === 1 ? 'true' : 'false',
    })
  }
  const handlePreauth = (value: number | undefined) => {
    updatePreAuthTickets({
      id: preAuthTicketsId,
      value: value === 1 ? 'true' : 'false',
    })
  }

  const handleCarrierMultiAssignmentUpdate = (value: number | undefined) => {
    if (carrierMultiAssignmentId == null) return
    updateCarrierMultiAssignment({
      id: carrierMultiAssignmentId,
      value: value === 1 ? 'true' : 'false',
    })
  }

  const handleRequirementCCUpdate = (form: FormType) => {
    if (requirementCCId) {
      updateRequirementCC({ id: requirementCCId, value: form.requirementCC })
      // Update the value to disable the button after submission
      setNewRequirementCCValue(requirementCCValue || '')
    }
  }
  const handleAllocationChange = (value: any) => {
    setValue('allocation', value as number)
    setNewAllocationTime(value as number)
  }

  useEffect(() => {
    if (allocationMinutes) setValue('allocation', allocationMinutes)
    setNewAllocationTime(allocationMinutes)
    if (deleteTicketsDays) setValue('ticketDays', deleteTicketsDays)
    setNewDeleteTicketDays(deleteTicketsDays)
    if (requirementCCValue) setValue('requirementCC', String(requirementCCValue))
    setNewRequirementCCValue(String(requirementCCValue))
    const currentCutoffTime =
      typeof cutoffTimeHour === 'number' && !Number.isNaN(cutoffTimeHour)
        ? `${String(cutoffTimeHour).padStart(2, '0')}:00`
        : CutoffTimeUtils.getCutoffTime()
    setValue('cutoffTime', currentCutoffTime)
    setNewCutoffTime(currentCutoffTime)
    const automaticBatchCloseDays = parseAutomaticBatchCloseDays(automaticBillingBatchCloseDays)
    setValue('automaticBillingBatchCloseDays', automaticBatchCloseDays)
    setNewAutomaticBatchCloseDays(automaticBatchCloseDays)
  }, [
    allocationMinutes,
    automaticBillingBatchCloseDays,
    cutoffTimeHour,
    deleteTicketsDays,
    requirementCCValue,
    setValue,
  ])

  const handlerequirementCCId = (value: string) => {
    setValue('requirementCC', value)
    setNewRequirementCCValue(value)

    // Clear the error if the field is empty or if the value is válido
    if (!value || validateAlphanumeric(value)) {
      setCcRequirementError('')
    }
  }

  const handleDeleteTicketsChange = (value: any) => {
    setValue('ticketDays', value as number)
    setNewDeleteTicketDays(value as number)
  }

  const handleCutoffTimeChange = (value: any) => {
    const formattedValue = value
      ? dayjs(value).format('HH:mm')
      : CutoffTimeUtils.DEFAULT_CUTOFF_TIME
    setValue('cutoffTime', formattedValue)
    setNewCutoffTime(formattedValue)
  }

  const handleCutoffTimeUpdate = (form: FormType) => {
    if (cutoffTimeId == null) return
    const cutoffHour = dayjs(form.cutoffTime, 'HH:mm').hour()
    updateCutoffTime({
      id: cutoffTimeId,
      value: cutoffHour,
    })
    const normalizedCutoffTime = `${String(cutoffHour).padStart(2, '0')}:00`
    CutoffTimeUtils.setCutoffTime(normalizedCutoffTime)
    setValue('cutoffTime', normalizedCutoffTime)
    setNewCutoffTime(normalizedCutoffTime)
  }

  const handleAutomaticBatchCloseDaysChange = (values: string[]) => {
    const normalizedDays = parseAutomaticBatchCloseDays(values.join(','))
    setValue('automaticBillingBatchCloseDays', normalizedDays)
    setNewAutomaticBatchCloseDays(normalizedDays)
  }

  const handleAutomaticBatchCloseDaysUpdate = (form: FormType) => {
    if (automaticBillingBatchCloseDaysId == null) return
    const formattedDays = formatAutomaticBatchCloseDays(form.automaticBillingBatchCloseDays)
    if (!formattedDays) return
    updateAutomaticBillingBatchCloseDays({
      id: automaticBillingBatchCloseDaysId,
      value: formattedDays,
    })
    const parsedDays = parseAutomaticBatchCloseDays(formattedDays)
    setValue('automaticBillingBatchCloseDays', parsedDays)
    setNewAutomaticBatchCloseDays(parsedDays)
  }

  const validateAlphanumeric = (value: string) => /^[a-zA-Z0-9]+$/.test(value)

  const [ccRequirementError, setCcRequirementError] = useState<string>('')

  // const handleCcRequirementChange = (value: any) => {
  //   const stringValue = String(value || '')
  //   if (stringValue && !validateAlphanumeric(stringValue)) {
  //     setCcRequirementError(
  //       t('admin_router.admin_dashboard.settings.alphanumeric_validation_message'),
  //     )
  //   } else {
  //     setCcRequirementError('')
  //   }
  // }

  const handleCcRequirementKeyDown = (e: React.KeyboardEvent) => {
    if (!validateAlphanumeric(e.key)) {
      e.preventDefault()
      setCcRequirementError(
        t('admin_router.admin_dashboard.settings.alphanumeric_validation_message'),
        // Show error immediately when an invalid key is pressed
      )
    }
  }

  return (
    <Container>
      <TitleAndDivider title={t('admin_router.admin_dashboard.settings.title')} />

      <Spin
        spinning={
          isLoading ||
          isConfigLoadingTime ||
          isConfigLoadingPercentaje ||
          isConfigLoadingRequirementCC ||
          isConfigLoadingCutoffTime ||
          isConfigLoadingAutomaticBillingBatchCloseDays
        }
      >
        <StyledRow>
          <Col span={8}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.assignation_time')}:{' '}
              <Tooltip
                title={t(
                  'admin_router.admin_dashboard.settings.time_for_assignment_without_category',
                )}
              >
                <InfoCircleOutlined style={{ marginLeft: '2px' }} />
              </Tooltip>
            </StyledText>
          </Col>
          <Col span={2}>
            <Controller
              name="allocation"
              control={control}
              defaultValue={allocationMinutes}
              render={({ field }) => (
                <StyledInputNumber
                  min={0}
                  value={field.value}
                  onChange={handleAllocationChange}
                  disabled={roles?.includes(ADMINRO)}
                />
              )}
            />
          </Col>
          <Col span={3}>
            <Button
              onClick={handleSubmit(handleAllocationUpdate)}
              disabled={
                allocationMinutes === newAllocationTime ||
                newAllocationTime === null ||
                roles?.includes(ADMINRO)
              }
            >
              {t('ta_invoice.new_invoice.update')}
            </Button>
          </Col>
        </StyledRow>
        <StyledRow>
          <Col span={8}>
            <StyledText>{t('admin_router.admin_dashboard.settings.ticket_days')}:</StyledText>
          </Col>
          <Col span={2}>
            {roles?.includes(ADMINRO) ? (
              <Controller
                name="ticketDays"
                control={control}
                defaultValue={deleteTicketsDays}
                render={({ field }) => (
                  <StyledInputNumber
                    min={0}
                    max={100}
                    value={field.value}
                    onChange={handleDeleteTicketsChange}
                    disabled
                  />
                )}
              />
            ) : (
              <Controller
                name="ticketDays"
                control={control}
                defaultValue={deleteTicketsDays}
                render={({ field }) => (
                  <StyledInputNumber
                    min={0}
                    max={100}
                    value={field.value}
                    onChange={handleDeleteTicketsChange}
                  />
                )}
              />
            )}
          </Col>
          <Col span={3}>
            <Button
              onClick={handleSubmit(handleDeleteTicketsUpdate)}
              disabled={
                deleteTicketsDays === newDeleteTicketDays ||
                newDeleteTicketDays === null ||
                roles?.includes(ADMINRO)
              }
            >
              {t('ta_invoice.new_invoice.update')}
            </Button>
          </Col>
        </StyledRow>
        <StyledRow>
          <Col span={8}>
            <StyledText>{t('admin_router.admin_dashboard.settings.cutoff_time')}:</StyledText>
          </Col>
          <Col span={2}>
            <Controller
              name="cutoffTime"
              control={control}
              defaultValue={CutoffTimeUtils.getCutoffTime()}
              render={({ field }) => (
                <TimePicker
                  format="HH:mm"
                  value={field.value ? dayjs(field.value, 'HH:mm') : undefined}
                  onChange={handleCutoffTimeChange}
                  disabled={roles?.includes(ADMINRO)}
                />
              )}
            />
          </Col>
          <Col span={3}>
            <Button
              onClick={handleSubmit(handleCutoffTimeUpdate)}
              disabled={
                cutoffTimeId == null ||
                CutoffTimeUtils.getCutoffTime() === newCutoffTime ||
                roles?.includes(ADMINRO)
              }
            >
              {t('ta_invoice.new_invoice.update')}
            </Button>
          </Col>
        </StyledRow>
        <SettingsRowTight>
          <Col span={8}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.automatic_batch_close_days')}:
              <Tooltip
                title={t(
                  'admin_router.admin_dashboard.settings.automatic_batch_close_days_tooltip',
                )}
              >
                <InfoCircleOutlined style={{ marginLeft: '5px' }} />
              </Tooltip>
            </StyledText>
          </Col>
          <Col span={6}>
            <Controller
              name="automaticBillingBatchCloseDays"
              control={control}
              defaultValue={[]}
              render={({ field }) => (
                <Select
                  mode="multiple"
                  maxTagCount="responsive"
                  options={automaticBatchCloseDayOptions}
                  placeholder={t(
                    'admin_router.admin_dashboard.settings.automatic_batch_close_days_placeholder',
                  )}
                  value={field.value}
                  onChange={handleAutomaticBatchCloseDaysChange}
                  disabled={automaticBillingBatchCloseDaysId == null || roles?.includes(ADMINRO)}
                  style={{ width: '100%' }}
                />
              )}
            />
            {automaticBillingBatchCloseDaysId == null && !isLoading && (
              <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: '4px' }}>
                {t('admin_router.admin_dashboard.settings.automatic_batch_close_days_missing')}
              </div>
            )}
          </Col>
          <Col span={3}>
            <Button
              style={{ marginLeft: '0.5rem' }}
              onClick={handleSubmit(handleAutomaticBatchCloseDaysUpdate)}
              disabled={
                automaticBillingBatchCloseDaysId == null ||
                newAutomaticBatchCloseDays.length === 0 ||
                formatAutomaticBatchCloseDays(newAutomaticBatchCloseDays) ===
                  formatAutomaticBatchCloseDays(
                    parseAutomaticBatchCloseDays(automaticBillingBatchCloseDays),
                  ) ||
                roles?.includes(ADMINRO)
              }
            >
              {t('ta_invoice.new_invoice.update')}
            </Button>
          </Col>
        </SettingsRowTight>
        <StyledRow>
          <Col span={8}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.tickets_assignation')}:
              <Tooltip title={t('admin_router.admin_dashboard.settings.set_default_travel_option')}>
                <InfoCircleOutlined style={{ marginLeft: '5px' }} />
              </Tooltip>
            </StyledText>
          </Col>
          {roles?.includes(ADMINRO) ? (
            <Radio.Group size="large" value={assignmentTickets === 'true' ? 1 : 0}>
              <Col span={24}>
                <Radio
                  value={1}
                  onChange={e => handleAssignmentTicketsUpdate(e.target.value)}
                  disabled
                >
                  {t('admin_router.admin_dashboard.settings.tickets_assig_automatic')}
                </Radio>
                <Radio
                  value={0}
                  onChange={e => handleAssignmentTicketsUpdate(e.target.value)}
                  disabled
                >
                  {t('admin_router.admin_dashboard.settings.tickets_assig_manual')}
                </Radio>
              </Col>
            </Radio.Group>
          ) : (
            <Radio.Group size="large" value={assignmentTickets === 'true' ? 1 : 0}>
              <Col span={24}>
                <Radio value={1} onChange={e => handleAssignmentTicketsUpdate(e.target.value)}>
                  {t('admin_router.admin_dashboard.settings.tickets_assig_automatic')}
                </Radio>
                <Radio value={0} onChange={e => handleAssignmentTicketsUpdate(e.target.value)}>
                  {t('admin_router.admin_dashboard.settings.tickets_assig_manual')}
                </Radio>
              </Col>
            </Radio.Group>
          )}
        </StyledRow>
        <StyledRow>
          <Col span={8}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.preauth_tickets')}:
              <Tooltip title={t('admin_router.admin_dashboard.settings.pre_authorization_meaning')}>
                <InfoCircleOutlined style={{ marginLeft: '5px' }} />
              </Tooltip>
            </StyledText>
          </Col>
          {roles?.includes(ADMINRO) ? (
            <Radio.Group size="large" value={preAuthTickets === 'true' ? 1 : 0}>
              <Col span={24}>
                <Radio value={1} onChange={e => handlePreauth(e.target.value)} disabled>
                  {t('admin_router.admin_dashboard.settings.yes')}
                </Radio>
                <Radio value={0} onChange={e => handlePreauth(e.target.value)} disabled>
                  {t('admin_router.admin_dashboard.settings.no')}
                </Radio>
              </Col>
            </Radio.Group>
          ) : (
            <Radio.Group size="large" value={preAuthTickets === 'true' ? 1 : 0}>
              <Col span={24}>
                <Radio value={1} onChange={e => handlePreauth(e.target.value)}>
                  {t('admin_router.admin_dashboard.settings.yes')}
                </Radio>
                <Radio value={0} onChange={e => handlePreauth(e.target.value)}>
                  {t('admin_router.admin_dashboard.settings.no')}
                </Radio>
              </Col>
            </Radio.Group>
          )}
        </StyledRow>
        <StyledRow>
          <Col span={8}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.carrier_multi_assignment')}:
              <Tooltip
                title={t('admin_router.admin_dashboard.settings.carrier_multi_assignment_tooltip')}
              >
                <InfoCircleOutlined style={{ marginLeft: '5px' }} />
              </Tooltip>
            </StyledText>
          </Col>
          {roles?.includes(ADMINRO) ? (
            <Radio.Group size="large" value={carrierMultiAssignmentEnabled === 'true' ? 1 : 0}>
              <Col span={24}>
                <Radio value={1} disabled>
                  {t('admin_router.admin_dashboard.settings.yes')}
                </Radio>
                <Radio value={0} disabled>
                  {t('admin_router.admin_dashboard.settings.no')}
                </Radio>
              </Col>
            </Radio.Group>
          ) : (
            <Radio.Group size="large" value={carrierMultiAssignmentEnabled === 'true' ? 1 : 0}>
              <Col span={24}>
                <Radio
                  value={1}
                  onChange={e => handleCarrierMultiAssignmentUpdate(e.target.value)}
                  disabled={carrierMultiAssignmentId == null}
                >
                  {t('admin_router.admin_dashboard.settings.yes')}
                </Radio>
                <Radio
                  value={0}
                  onChange={e => handleCarrierMultiAssignmentUpdate(e.target.value)}
                  disabled={carrierMultiAssignmentId == null}
                >
                  {t('admin_router.admin_dashboard.settings.no')}
                </Radio>
              </Col>
            </Radio.Group>
          )}
        </StyledRow>
        {/* //TODO: new field - Only visible to non-ADMIN roles */}
        {!roles?.includes(ADMINRO) && (
          <>
            <SettingsTitleRow style={{ marginTop: '2.25rem' }}>
              <TitleAndDivider
                title={t('admin_router.admin_dashboard.settings.title_certification')}
              />
            </SettingsTitleRow>
            <SettingsRowTight>
              <Col span={8}>
                <StyledText>
                  {t('admin_router.admin_dashboard.settings.cc_requirement')}:{' '}
                  {/* <Tooltip
                    title={t(
                      'admin_router.admin_dashboard.settings.time_for_assignment_without_category',
                    )}
                  >
                    <InfoCircleOutlined style={{ marginLeft: '2px' }} />
                  </Tooltip> */}
                </StyledText>
              </Col>
              <Col span={2}>
                <Form.Item
                  validateStatus={ccRequirementError ? 'error' : ''}
                  help={ccRequirementError}
                  style={{ marginBottom: 0 }}
                >
                  <Controller
                    name="requirementCC"
                    control={control}
                    defaultValue={requirementCCValue ? String(requirementCCValue) : ''}
                    render={({ field }) => (
                      <Input
                        className="input-number-requirent"
                        value={field.value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handlerequirementCCId(e.target.value)
                        }
                        onKeyDown={handleCcRequirementKeyDown}
                      />
                    )}
                  />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Button
                  className="button-disabled-normal"
                  style={{ marginLeft: '0.5rem' }}
                  onClick={handleSubmit(handleRequirementCCUpdate)}
                  disabled={
                    String(requirementCCValue) === newRequirementCCValue ||
                    newRequirementCCValue === null ||
                    newRequirementCCValue === undefined ||
                    !validateAlphanumeric(newRequirementCCValue)
                  }
                >
                  {t('ta_invoice.new_invoice.update')}
                </Button>
              </Col>
            </SettingsRowTight>
          </>
        )}
      </Spin>
    </Container>
  )
}
export { Settings }
